import React, { useEffect, useMemo } from 'react';

import { Table } from '~/components';
import { useLoading } from '~/hooks/loading';
import useQuery from '~/hooks/useQuery';
import { FinancesDataType, IFinancesData } from '~/models/Boss';
import ExplainDate from '~/pages/Billing/ExplainDate';
import { TableHeaders } from '~/pages/Billing/TableInformation/tableHeaders';
import { IBossService } from '~/services';
import { getPaymentStatus } from '~/utils/financeReports';
import { fullMonthsConverted } from '~/utils/months';

import * as S from './styles';

const TableInformation: React.FC = () => {
  const { setLoading } = useLoading();
  const [data, setData] = React.useState<IFinancesData>({} as IFinancesData);
  const query = useQuery();

  const foundHeader = TableHeaders.find((header) =>
    window.location.pathname.includes(header.key),
  );
  const colNames = foundHeader?.values ?? [];

  const queryValue = useMemo(() => {
    const q = new URLSearchParams();
    const initial = query.get('initialDate');
    const last = query.get('lastDate');

    if (initial) q.append('start', initial);
    if (last) q.append('end', last);

    return { query: q, initial, last };
  }, [query]);

  const getData = async () => {
    try {
      setLoading(true);

      let vindPaymentStatus = null;
      let vindReportsFinances = null;
      let vindPaymentMethods = null;
      let vindPaymentByMonthAndYear = null;

      const today = new Date().toISOString().split('T')[0];

      vindPaymentStatus = await IBossService.getVindiPaymentStatus(
        `start=${queryValue.initial}&end=${
          queryValue.last ? queryValue.last : today
        }`,
      );
      vindReportsFinances = await IBossService.getVindiReportsFinances(
        `start=${queryValue.initial}&end=${
          queryValue.last ? queryValue.last : today
        }`,
      );
      vindPaymentMethods = await IBossService.getVindiPaymentMethods(
        `start=${queryValue.initial}&end=${
          queryValue.last ? queryValue.last : today
        }`,
      );
      vindPaymentByMonthAndYear = await IBossService.getVindiPaymentByMonthAndYear(
        `start=${queryValue.initial}&end=${
          queryValue.last ? queryValue.last : today
        }`,
      );

      const response = await IBossService.getReportsFinances(
        queryValue.query.toString(),
      );

      const formatedData = {
        countAndSumSubscriptionsByStatus: vindPaymentStatus.data,
        countAndSumSubscriptionsByPlanName: vindReportsFinances.data,
        countAndSumSubscriptionsByPaymentMethod: vindPaymentMethods.data,
        countAndSumPaymentsByMonthAndYear: vindPaymentByMonthAndYear.data,
      };

      setData(formatedData);

      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log({ error });
    }
  };

  const renderNumCurrency = (num: string | number) => {
    if (!num) return 'R$ 0';
    const number = typeof num === 'string' ? Number(num) : num;

    return number.toLocaleString('pt-br', {
      style: 'currency',
      currency: 'BRL',
    });
  };

  const renderComercialPlanTable = () => {
    if (data.countAndSumSubscriptionsByPlanName.length === 0)
      return <S.NoContentMessage>Não há dados cadastrados</S.NoContentMessage>;

    return data?.countAndSumSubscriptionsByPlanName?.map((item) => (
      <tr key={item.planName}>
        <td>{item.planName}</td>
        <td>{item.count}</td>
        <td>{renderNumCurrency(item.total)}</td>
      </tr>
    ));
  };

  const renderPaymentFormTable = () => {
    if (!data) return <></>;

    return data?.countAndSumSubscriptionsByPaymentMethod.map((item) => (
      <tr key={item.paymentMethod}>
        <td>{item.paymentMethod}</td>
        <td>{item.count}</td>
        <td>{renderNumCurrency(item.total)}</td>
      </tr>
    ));
  };

  const renderStatusTable = () => {
    if (!data) return <></>;

    return data?.countAndSumSubscriptionsByStatus.map((item) => (
      <tr key={item.paymentStatus}>
        <td>{getPaymentStatus(item.paymentStatus)}</td>
        <td>{item.count}</td>
        <td>{renderNumCurrency(item.total)}</td>
      </tr>
    ));
  };

  const renderStatusFinanceTable = () => {
    if (!data) return <></>;

    return data?.countAndSumPaymentsByMonthAndYear.map((item) => (
      <tr key={item.total + item.month}>
        <td>
          {
            fullMonthsConverted[
              parseInt(
                item.month.split('-')[1],
                10,
              ) as keyof typeof fullMonthsConverted
            ]
          }
        </td>
        <td>{item.count}</td>
        <td>{renderNumCurrency(item.total)}</td>
      </tr>
    ));
  };

  const renderSelectedTable = () => {
    if (foundHeader?.dataKey === FinancesDataType.PAYMENTS_BY_MONTH_AND_YEAR) {
      return renderStatusFinanceTable();
    }

    if (
      foundHeader?.dataKey === FinancesDataType.SUBSCRIPTIONS_BY_PAYMENT_METHOD
    ) {
      return renderPaymentFormTable();
    }

    if (foundHeader?.dataKey === FinancesDataType.SUBSCRIPTIONS_BY_PLAN_NAME) {
      return renderComercialPlanTable();
    }

    if (foundHeader?.dataKey === FinancesDataType.SUBSCRIPTIONS_BY_STATUS) {
      return renderStatusTable();
    }

    return <></>;
  };

  useEffect(() => {
    getData();
  }, []);

  console.log('data:', data);

  return (
    <S.Container>
      <S.Content>
        <ExplainDate
          initial={query.get('initialDate') || ''}
          last={query.get('lastDate') || ''}
        />
        {data[foundHeader?.dataKey as keyof typeof data] &&
        data[foundHeader?.dataKey as keyof typeof data].length > 0 ? (
          <>
            {Object?.keys(data).length > 0 && (
              <Table colsNames={colNames}>{renderSelectedTable()}</Table>
            )}
          </>
        ) : (
          <S.NoContentMessage>
            Nenhum dado cadastrado nesse período
          </S.NoContentMessage>
        )}
      </S.Content>
    </S.Container>
  );
};

export default TableInformation;
